import { monthPoints, trendSummary } from './reportCopy';

// Pure calculation behind the Incident Remediation report: no React, no API calls, no wording.
// Input is one document per Problem from /api/incident-remediation/problems. The sentences the page shows
// are built in reportCopy.js from the numbers worked out here.

/**
 * The report runs from the start date through the end of the current year (UTC), newest year first.
 * The start date comes from the API (reportStart), derived from the Admin-saved IR discovery query.
 */
const getReportRange = (now = new Date(), start) => {
  const first = new Date(start);
  if (!start || Number.isNaN(first.getTime())) throw new Error('The report start date is missing or not a valid date.');
  const end = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1));
  const currentYear = end.getUTCFullYear() - 1;
  const years = [];
  for (let year = currentYear; year >= first.getUTCFullYear(); year -= 1) years.push(year);
  return { start: first, end, currentYear, years };
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const inReportRange = (value, range) => {
  const date = value ? new Date(value) : null;
  return Boolean(date) && !Number.isNaN(date.getTime()) && date >= range.start && date < range.end;
};

// Months are always read in UTC, so a problem near midnight lands in the same month for every viewer.
const monthKey = (value) => {
  const opened = new Date(value);
  return `${opened.getUTCFullYear()}-${String(opened.getUTCMonth() + 1).padStart(2, '0')}`;
};

const closedDaysBetween = (openedAt, closedAt) => {
  if (!openedAt || !closedAt) return null;
  const opened = new Date(openedAt);
  const closed = new Date(closedAt);
  if (Number.isNaN(opened.getTime()) || Number.isNaN(closed.getTime()) || closed < opened) return null;
  return Math.floor((closed.getTime() - opened.getTime()) / 86400000);
};

/**
 * API rows (one per Problem) turned into the shape the month and table code reads.
 *  - Month / "Opened": the Problem's creation date. It falls back to the earliest linked incident only when
 *    the Problem date was never synced. The same date drives the month bucket and the table.
 *  - Open / closed: isOpen is decided once on the server when the Problem is stored, so every screen agrees.
 *  - Closed date: closedAt, else resolvedAt (a Resolved problem has no closed date yet).
 *  - Priority, team and impact come from one representative linked incident (a major incident first, then the earliest).
 *  - Impact hours/days are that incident's calendar duration (how long it ran), not customer count.
 */
const normalizeProblem = (row) => {
  const minutes = row.impactMinutes == null || row.impactMinutes === '' ? null : Number(row.impactMinutes);
  const hasImpact = minutes != null && Number.isFinite(minutes);
  const incidentList = (row.incidentNumbers || []).filter(Boolean);
  return {
    problemNumber: row.number,
    problemShortDescription: row.shortDescription || '',
    problemState: row.state || '',
    problemRca: row.rca || '',
    workaround: row.workaround || '',
    fixNotes: row.fixNotes || '',
    dueAt: row.dueAt || null,
    isOpen: row.isOpen === true,
    major: row.major === true,
    openedAt: row.openedAt || row.firstIncidentOpenedAt || null,
    problemOpenedAt: row.openedAt || null,
    problemClosedAt: row.closedAt || row.resolvedAt || null,
    teamName: row.teamName || '',
    assignedTo: row.assignedTo || '',
    priority: row.priority || '',
    impactHours: hasImpact ? Math.round((minutes / 60) * 100) / 100 : null,
    impactDays: hasImpact ? Math.floor(minutes / 1440) : null,
    incidentNumbers: incidentList.join(', ')
  };
};

const buildProblems = (rows, range) => (rows || [])
  .filter((row) => row && row.number)
  .map(normalizeProblem)
  // A Problem created before the report starts has no month to be counted in.
  .filter((problem) => inReportRange(problem.openedAt, range));

/** What the page shows before the first load: no months, so nothing to draw. */
export const emptyReport = Object.freeze({ groups: [], chart: [], problemFacts: [] });

/**
 * Builds everything the page shows from the stored Problems.
 * @param {object[]} rows Problem rows from GET /api/incident-remediation/problems
 * @param {{now?: Date, reportStart: string}} options reportStart is the server's start date; now is only for tests
 */
export const buildReport = (rows, { now = new Date(), reportStart } = {}) => {
  const range = getReportRange(now, reportStart);
  const problems = buildProblems(rows, range);

  const months = [];
  range.years.forEach((year) => {
    for (let month = 11; month >= 0; month -= 1) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;
      const monthRows = problems.filter((problem) => monthKey(problem.openedAt) === key);
      // Calendar closes in this month (answers "how many did we close in September?").
      const closedThisMonth = problems.filter((problem) => (
        !problem.isOpen
        && problem.problemClosedAt
        && monthKey(problem.problemClosedAt) === key
      ));
      if (!monthRows.length && !closedThisMonth.length) continue;
      const problemsOpen = monthRows.filter((problem) => problem.isOpen);
      const monthStart = new Date(Date.UTC(year, month, 1));
      const earlierOpenTickets = [];
      problems.forEach((problem) => {
        const opened = new Date(problem.openedAt);
        if (Number.isNaN(opened.getTime()) || opened >= monthStart || !problem.isOpen) return;
        earlierOpenTickets.push(problem);
      });
      // Problems from earlier months that are still open, grouped by the month they were opened in
      const earlierBuckets = new Map();
      earlierOpenTickets.forEach((problem) => {
        const bucketKey = monthKey(problem.openedAt);
        const opened = new Date(problem.openedAt);
        const bucket = earlierBuckets.get(bucketKey) || {
          key: bucketKey,
          label: MONTHS[opened.getUTCMonth()],
          year: opened.getUTCFullYear(),
          numbers: []
        };
        bucket.numbers.push(problem.problemNumber);
        earlierBuckets.set(bucketKey, bucket);
      });
      const earlierOpenByMonth = [...earlierBuckets.values()]
        .sort((a, b) => a.key.localeCompare(b.key))
        .map((bucket) => ({ ...bucket, numbers: bucket.numbers.sort() }));
      const major = monthRows.filter((problem) => problem.major).length;
      const majorOpen = problemsOpen.filter((problem) => problem.major).length;
      months.push({
        key,
        year,
        shortLabel: `${MONTHS_SHORT[month]} ${year}`,
        fullLabel: `${MONTHS[month]} ${year}`,
        opened: monthRows.length,
        closedThisMonth: closedThisMonth.length,
        closedThisMonthProblems: closedThisMonth,
        major,
        nonMajor: monthRows.length - major,
        majorOpen,
        nonMajorOpen: problemsOpen.length - majorOpen,
        problems: monthRows,
        problemsOpen,
        earlierOpenTickets,
        earlierOpenByMonth,
        attention: problemsOpen.length > 0
      });
    }
  });

  const groups = months.map((month) => {
    const seen = new Set();
    const rowsForMonth = [];
    [...month.problems, ...month.earlierOpenTickets]
      .sort((a, b) => {
        if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
        return new Date(b.openedAt) - new Date(a.openedAt);
      })
      .forEach((problem) => {
        if (seen.has(problem.problemNumber)) return;
        seen.add(problem.problemNumber);
        rowsForMonth.push({
          ...problem,
          classLabel: problem.major ? 'Major' : 'Non-major',
          problemClosedAt: problem.isOpen ? null : problem.problemClosedAt,
          closedDays: problem.isOpen ? null : closedDaysBetween(problem.problemOpenedAt, problem.problemClosedAt)
        });
      });
    return {
      key: month.key,
      label: month.fullLabel,
      shortLabel: month.shortLabel,
      opened: month.opened,
      closedThisMonth: month.closedThisMonth,
      major: month.major,
      nonMajor: month.nonMajor,
      majorOpen: month.majorOpen,
      nonMajorOpen: month.nonMajorOpen,
      openProblems: month.problemsOpen.length,
      attention: month.attention,
      points: monthPoints(month),
      rows: rowsForMonth
    };
  });

  const chart = [...groups].reverse().map((group) => ({
    key: group.key,
    tick: group.shortLabel.replace(' 20', ' '),
    monthName: group.label.split(' ')[0],
    year: group.label.split(' ')[1],
    opened: group.opened,
    // Opened that month and later closed (cohort). Not the same as closedThisMonth.
    closed: group.opened - group.openProblems,
    closedThisMonth: group.closedThisMonth,
    openProblems: group.openProblems,
    major: group.major,
    nonMajor: group.nonMajor,
    majorOpen: group.majorOpen,
    nonMajorOpen: group.nonMajorOpen
  }));

  const problemFacts = problems.map((problem) => ({
    number: problem.problemNumber,
    key: monthKey(problem.openedAt),
    state: problem.problemState,
    title: problem.problemShortDescription,
    teamName: problem.teamName,
    assignedTo: problem.assignedTo,
    rca: problem.problemRca,
    workaround: problem.workaround,
    fixNotes: problem.fixNotes,
    dueAt: problem.dueAt,
    openedAt: problem.openedAt,
    open: problem.isOpen,
    closedDays: problem.isOpen ? null : closedDaysBetween(problem.problemOpenedAt, problem.problemClosedAt)
  }));

  return { groups, chart, problemFacts };
};

/**
 * The report scoped to the selected month: every month up to and including activeKey.
 */
export const buildScope = (report, activeKey) => {
  const scopedChart = report.chart.filter((row) => activeKey && row.key <= activeKey);
  const scopedSum = (field) => scopedChart.reduce((total, row) => total + row[field], 0);
  const scopeResult = {
    chart: scopedChart,
    trend: trendSummary(scopedChart),
    total: scopedSum('opened'),
    major: scopedSum('major'),
    nonMajor: scopedSum('nonMajor'),
    majorOpen: scopedSum('majorOpen'),
    nonMajorOpen: scopedSum('nonMajorOpen'),
    majorClosed: scopedSum('major') - scopedSum('majorOpen'),
    nonMajorClosed: scopedSum('nonMajor') - scopedSum('nonMajorOpen'),
    majorOpenMonths: scopedChart.filter((row) => row.majorOpen > 0).map((row) => ({
      key: row.key,
      label: row.tick.split(' ')[0],
      count: row.majorOpen
    })),
    nonMajorOpenMonths: scopedChart.filter((row) => row.nonMajorOpen > 0).map((row) => ({
      key: row.key,
      label: row.tick.split(' ')[0],
      count: row.nonMajorOpen
    }))
  };
  const scopedFacts = (report.problemFacts || []).filter((fact) => activeKey && fact.key <= activeKey);
  const closedForAverage = scopedFacts.filter((fact) => fact.closedDays != null);
  const averageClosedDaysResult = closedForAverage.length
    ? Math.round((closedForAverage.reduce((sum, fact) => sum + fact.closedDays, 0) / closedForAverage.length) * 10) / 10
    : null;
  const stillOpenFactsResult = scopedFacts.filter((fact) => fact.open).sort((a, b) => new Date(a.openedAt) - new Date(b.openedAt) || a.number.localeCompare(b.number));
  return {
    scope: scopeResult,
    averageClosedDays: averageClosedDaysResult,
    closedForAverageCount: closedForAverage.length,
    stillOpenFacts: stillOpenFactsResult
  };
};
